import { Link } from 'react-router-dom';

import { Label, Tag } from '../components/primitives';
import { GlossaryProvider, Glossed } from '../components/Glossary';
import { TAXONOMY } from '../data/taxonomy';
import { EVIDENCE_CASE_LIST, formatCaseDate } from '../data/evidence-cases';
import { MODEL_LAYER_FAILURE_MODES } from '../engine/model';
import { PATTERNS_NOT_RUN } from '../data/analysisRun';
import './Method.css';

const DETERMINISTIC_FAILURE_MODES = TAXONOMY.failure_modes
  .map((fm) => fm.id)
  .filter((id) => !(MODEL_LAYER_FAILURE_MODES as string[]).includes(id));

/** The two documents the checks are built against. */
const STANDARDS = [
  {
    name: 'FEMP M&V Guidelines, Version 5.0',
    issued: 'February 2024',
    used: 'Table 3-1 (the RRP Matrix) for the row each finding belongs to; §2.2.4 for the required contents of a plan; §4.2.1.2 for the permitted and prohibited sources of an estimated parameter; §2.1.4 for static factors and non-routine adjustment thresholds.',
    url: 'https://www.energy.gov/sites/default/files/2024-03/femp-mv-5-0-guidelines_DRAFT.pdf',
  },
  {
    name: 'DOE ESPC IDIQ, Generations 1 to 4',
    issued: '1998, 2008, 2017, 2023',
    used: 'The award-date lookup that decides whether baseline witnessing was "should" (Gen 2 §C.4.6) or "shall" (Gen 3 and Gen 4). Ordering periods overlap, so a date falling in two is reported as falling in two.',
    url: 'https://www.energy.gov/femp/federal-energy-management-program',
  },
];

interface StageSpec {
  n: string;
  name: string;
  model: boolean;
  does: string;
  covers: string[];
  cannot: string[];
}

const STAGES: StageSpec[] = [
  {
    n: '1',
    name: 'Arithmetic',
    model: false,
    does: 'Arithmetic and lookups over the document as read. It recomputes the savings tables against each other, checks the external references it can identify for a version and a date, tests every parameter the plan marks as estimated against the permitted-source list — FEMP’s five, plus observation of the building, which this tool adds, and turns an award date into the witnessing obligation that governed it. Every finding carries the check itself: its inputs, the operation and the result, so it can be redone on paper.',
    covers: DETERMINISTIC_FAILURE_MODES,
    cannot: [
      'Read a clause it has no rule for. It matches published detection signals, not meaning.',
      'Reconcile tables without confirmation. PDF table extraction is imperfect, so it asks which table is the summary before computing anything.',
      'Determine the IDIQ generation without an award date. The date is supplied by you; nothing is inferred from a cover date.',
    ],
  },
  {
    n: '2',
    name: 'Pattern match',
    model: true,
    does: 'A language model is given a section of the document and the published detection signals for the checks arithmetic cannot reach, and asked one thing: does this text contain the clause, and if so, quote it. Retrieval and matching, not judgement.',
    covers: MODEL_LAYER_FAILURE_MODES as string[],
    cannot: [
      'Produce a finding without a verbatim quote. Anything it cannot point at in the document is discarded.',
      'Set a tier, a score, a confidence or a severity. Tier — whether a named federal audit records this cause, or only FEMP’s guidance warns about it — comes from the published catalogue of checks, not from the model.',
      'Have its prose shown to you. The only model-written string that reaches this interface is the quote, and only after the quote has been found in the document.',
      'Be relied on for completeness. It can miss a clause it should have caught, and nothing here measures how often.',
    ],
  },
  {
    n: '3',
    name: 'Clauses that only matter in combination',
    model: true,
    does: 'A witness who is not independent of the measured party fires only when two facts hold together: the document references an O&M contractor, subcontractor or third-party provider, and the witness role is described without any constraint on who may fill it. Both are decided from the document text after the pattern match, not asked of the model — but the clause still has to reach the join, which means the pattern match has to have found and quoted it first. This is a filter on that stage, not an independent check.',
    covers: ['FM-03'],
    cannot: [
      'Fire on one condition. A plan that simply never constrains the witness role is not a finding: almost none do, and reporting every one of them would be free and worthless.',
      'See a contractual relationship the document does not mention. If the O&M arrangement lives in a separate contract, this join cannot know about it.',
    ],
  },
];

export default function Method() {
  return (
    <GlossaryProvider>
      <div className="mt">
        <header className="mt__header">
          <div>
            <Label mono>Method</Label>
            <h1 className="t-display-2">How it works, and where it stops.</h1>
          </div>
          <div>
            <p className="mt__lede">
              Three stages, only one of which uses a language model. Everything the
              first stage produces can be checked with a calculator and the source
              PDF.
            </p>
            <p className="mt__guide-link">
              <Link to="/guide">New here? Read the two-minute guide &rarr;</Link>
            </p>
          </div>
        </header>

        {/* The whole page in one paragraph, for a reader who will not read the
            rest of it. Nothing here is a summary of a claim made below in
            weaker terms: it is the same three stages and the same boundary. */}
        <section className="mt__short">
          <Label>The short version</Label>
          <p className="mt__short-body">
            Ceiling does three kinds of work. It re-does the plan&rsquo;s own
            arithmetic — you can check that on paper. It looks up whether
            references are pinned to a version and a date. And it matches clause
            wording against fifteen patterns drawn from federal audits, quoting
            the clause it matched so you can judge it yourself. It never shows
            you a sentence a model wrote about your plan. The only model-written
            text that reaches this screen is a quote it found in your document.
          </p>
        </section>

        {/* ---------------- 1: the three stages ---------------- */}
        <section className="mt__section">
          <div className="mt__section-head">
            <Label mono>01</Label>
            <h2 className="t-section">The three stages</h2>
          </div>

          {STAGES.map((stage) => (
            <article key={stage.n} className="mt__layer">
              <header className="mt__layer-head">
                <span className="mt__layer-n">{stage.n}</span>
                <h3 className="mt__layer-name">{stage.name}</h3>
                <Tag tone={stage.model ? 'amber' : 'blue'} sans>
                  {stage.model ? 'Uses a model' : 'No model'}
                </Tag>
              </header>

              <div className="mt__layer-body">
                <p className="mt__prose">
                  <Glossed>{stage.does}</Glossed>
                </p>

                <div className="mt__field">
                  <Label>Checks it covers ({stage.covers.length})</Label>
                  <p className="mt__covers">{stage.covers.join(' · ')}</p>
                </div>

                <div className="mt__field">
                  <Label>What it cannot do</Label>
                  <ul className="mt__cannot">
                    {stage.cannot.map((line, i) => (
                      <li key={i}>
                        <Glossed>{line}</Glossed>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </section>

        {/* ---------------- 2: the quote gate ---------------- */}
        <section className="mt__section">
          <div className="mt__section-head">
            <Label mono>02</Label>
            <h2 className="t-section">The quote gate</h2>
          </div>

          <p className="mt__prose">
            A model asked to find clauses will sometimes return a clause that is
            not in the document — a paraphrase, a tidied-up version, or a sentence
            from some other plan. So no model output becomes a finding until its
            quote has been located in the document text.
          </p>
          <p className="mt__prose">
            The quote is sought three ways: exactly as returned, then with
            whitespace normalised on both sides, then with punctuation and
            ligatures folded — the differences a PDF text layer introduces.
            Anything still not found is dropped and counted. What survives is not
            the model&rsquo;s text but the document&rsquo;s: the accepted quote is
            re-read from the document at the position found. It is verbatim
            against the text extracted from the PDF, which is not quite the same
            as being findable in a single search — extraction joins lines, so a
            quote spanning a line break may have to be searched for in parts.
          </p>

          <div className="mt__gate">
            <Label>Where this stands in Ceiling</Label>
            <p className="mt__gate-line">{PATTERNS_NOT_RUN}</p>
            <p className="mt__gate-line">
              So there is no drop count to show you here, and there will not be
              one: the gate is what would protect you if the stage ran, and it
              is described above so that you can judge the protection rather
              than take a number on trust.
            </p>
          </div>
        </section>

        {/* ---------------- 3: sources ---------------- */}
        <section className="mt__section">
          <div className="mt__section-head">
            <Label mono>03</Label>
            <h2 className="t-section">Sources</h2>
          </div>

          <p className="mt__prose">
            Every finding that cites a case cites one of these. They are federal
            audit reports and published guidance, not commentary. One check is
            the exception — two tables reporting different savings for the same
            measure. It is Tier 1, meaning a federal audit records the cause, yet
            it cites no audit, because its ground truth is recomputation: the
            figures are in the document and the arithmetic settles it.
          </p>

          <ul className="mt__cases">
            {EVIDENCE_CASE_LIST.map((c) => (
              <li key={c.id} className="mt__case">
                <span className="mt__case-issuer">{c.issuer}</span>
                <span className="mt__case-number">{c.reportNumber ?? '—'}</span>
                <span className="mt__case-date">{formatCaseDate(c.date)}</span>
                <span className="mt__case-scale">{c.scale}</span>
                <a
                  className="mt__case-link"
                  href={c.url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Open ↗
                </a>
              </li>
            ))}
          </ul>

          <div className="mt__field">
            <Label>Standards the checks are built on</Label>
            <ul className="mt__standards">
              {STANDARDS.map((s) => (
                <li key={s.name}>
                  <span className="mt__standard-name">
                    <a href={s.url} target="_blank" rel="noreferrer noopener">
                      {s.name}
                    </a>
                    <span className="mt__standard-issued">{s.issued}</span>
                  </span>
                  <span className="mt__standard-used">
                    <Glossed>{s.used}</Glossed>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ---------------- 4: limits ---------------- */}
        <section className="mt__section mt__section--limits" id="limits">
          <div className="mt__section-head">
            <Label mono>04</Label>
            <h2 className="t-section">Limits</h2>
          </div>

          <ol className="mt__limits">
            <li>
              <h3 className="mt__limit-head">
                Fifteen checks run. A clean report is not a clean plan.
              </h3>
              <p className="mt__prose">
                The fifteen cover causes drawn from thirty federal audits. A plan
                can be silent on all fifteen and still produce a dispute for a
                reason nobody has catalogued. Zero findings means these fifteen
                checks found nothing, and nothing more than that.
              </p>
            </li>

            <li>
              <h3 className="mt__limit-head">
                PDF table extraction is imperfect, which is why reconciliation asks
                for confirmation.
              </h3>
              <p className="mt__prose">
                Reconstructing a table from a PDF text layer misreads columns,
                loses headers and merges cells. A misread cell would produce a
                confident wrong finding, so the reconciliation check shows you
                the figures as it read them and asks which table is the summary
                before it computes anything. Any figure can be corrected, and a
                corrected figure is marked as corrected in the check.
              </p>
            </li>

            <li>
              <h3 className="mt__limit-head">Scanned documents are not supported.</h3>
              <p className="mt__prose">
                The tool reads the text layer of a PDF. A scan or a photocopy has
                no text layer, and there is no OCR here. Such a file is refused
                with an explanation rather than read to zero findings.
              </p>
            </li>

            <li>
              <h3 className="mt__limit-head">
                Tier 2 modes are warned about by the standard but have no audit
                case attached yet.
              </h3>
              <p className="mt__prose">
                {TAXONOMY.failure_modes.filter((fm) => fm.tier === 2).length} of the{' '}
                {TAXONOMY.failure_modes.length} checks are Tier 2. FEMP&rsquo;s own
                guidance warns about each, and in several the clause appears in
                FEMP&rsquo;s own model plan — but no federal audit has yet been
                found recording that cause producing a loss. A finding from one of
                them carries the badge &ldquo;Guidance warning&rdquo;, and it
                should carry less weight than one badged &ldquo;Audit
                precedent&rdquo;, where a named audit does record the cause.
              </p>
            </li>

            <li>
              <h3 className="mt__limit-head">
                This tool quotes and cites. It does not give legal or contractual
                advice.
              </h3>
              <p className="mt__prose">
                It quotes a clause and names the audit where the same cause
                produced a documented loss. It does not tell you what the clause
                means for your contract, whether to sign, what to negotiate, or
                what language to substitute — the moment it proposed replacement
                language it would be drafting, and the traceability that makes it
                worth anything would stop applying. Decisions about a contract
                remain with the contracting officer and counsel.
              </p>
            </li>
          </ol>

          <p className="mt__no-benchmark">
            There are no accuracy figures on this page because none have been
            measured. The tool has been run against DOE/FEMP&rsquo;s own model M&amp;V
            plan and two other federal documents; that is not a benchmark, and no
            claim about precision or recall should be read into it.
          </p>
        </section>
      </div>
    </GlossaryProvider>
  );
}
